import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Input } from '@/components/ui/input';
import { Link, Search, AlertCircle } from 'lucide-react';

interface InsertParameterDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  parameterName: string;
  parameterType: 'path' | 'query';
  currentEndpoint: string;
  onInsert: (newEndpoint: string) => void;
}

export const InsertParameterDialog: React.FC<InsertParameterDialogProps> = ({
  open,
  onOpenChange,
  parameterName,
  parameterType,
  currentEndpoint,
  onInsert,
}) => {
  const [insertMode, setInsertMode] = useState<'auto' | 'manual'>('auto');
  const [manualInput, setManualInput] = useState('');

  const handleInsert = () => {
    if (insertMode === 'auto') {
      // Auto-insert based on parameter type
      let newEndpoint = currentEndpoint;

      if (parameterType === 'path') {
        // Add to path - append to existing path
        const [pathPart, queryPart] = currentEndpoint.split('?');
        const pathWithParam = pathPart.endsWith('/')
          ? `${pathPart}{${parameterName}}`
          : `${pathPart}/{${parameterName}}`;
        newEndpoint = queryPart ? `${pathWithParam}?${queryPart}` : pathWithParam;
      } else {
        // Add to query string
        const [pathPart, queryPart] = currentEndpoint.split('?');
        const queryWithParam = queryPart
          ? `${queryPart}&${parameterName}={${parameterName}}`
          : `${parameterName}={${parameterName}}`;
        newEndpoint = `${pathPart}?${queryWithParam}`;
      }

      onInsert(newEndpoint);
    } else {
      // Manual mode - use what user typed
      onInsert(manualInput);
    }

    onOpenChange(false);
    setInsertMode('auto');
    setManualInput('');
  };

  const getAutoPreview = () => {
    if (parameterType === 'path') {
      const [pathPart] = currentEndpoint.split('?');
      const preview = pathPart.endsWith('/')
        ? `${pathPart}{${parameterName}}`
        : `${pathPart}/{${parameterName}}`;
      return preview;
    } else {
      const [pathPart, queryPart] = currentEndpoint.split('?');
      const queryPreview = queryPart
        ? `${queryPart}&${parameterName}={${parameterName}}`
        : `${parameterName}={${parameterName}}`;
      return `${pathPart}?${queryPreview}`;
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {parameterType === 'path' ? (
              <Link className="h-5 w-5 text-blue-600" />
            ) : (
              <Search className="h-5 w-5 text-green-600" />
            )}
            Insert Parameter into Endpoint
          </DialogTitle>
          <DialogDescription>
            How would you like to add <code className="font-mono font-semibold">{parameterName}</code> to your endpoint URL?
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <RadioGroup value={insertMode} onValueChange={(v) => setInsertMode(v as 'auto' | 'manual')}>
            {/* Auto Insert Option */}
            <div className="flex items-start space-x-3 space-y-0 rounded-md border p-4">
              <RadioGroupItem value="auto" id="auto" className="mt-1" />
              <div className="flex-1">
                <Label htmlFor="auto" className="font-medium cursor-pointer">
                  ✨ Smart Insert (Recommended)
                </Label>
                <p className="text-sm text-gray-500 mt-1">
                  {parameterType === 'path'
                    ? 'Append to URL path automatically'
                    : 'Add to query string automatically'}
                </p>
                {insertMode === 'auto' && (
                  <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded">
                    <p className="text-xs font-medium text-blue-900 mb-1">Preview:</p>
                    <code className="text-xs text-blue-700 font-mono break-all">
                      {getAutoPreview()}
                    </code>
                  </div>
                )}
              </div>
            </div>

            {/* Manual Insert Option */}
            <div className="flex items-start space-x-3 space-y-0 rounded-md border p-4">
              <RadioGroupItem value="manual" id="manual" className="mt-1" />
              <div className="flex-1">
                <Label htmlFor="manual" className="font-medium cursor-pointer">
                  ✏️ Manual Entry
                </Label>
                <p className="text-sm text-gray-500 mt-1">
                  Type the complete endpoint URL yourself
                </p>
                {insertMode === 'manual' && (
                  <div className="mt-3">
                    <Input
                      placeholder={`e.g., /customers/{customer_id}/${parameterName === 'customer_id' ? 'orders' : `{${parameterName}}`}`}
                      value={manualInput}
                      onChange={(e) => setManualInput(e.target.value)}
                      className="font-mono text-sm"
                    />
                  </div>
                )}
              </div>
            </div>
          </RadioGroup>

          {/* Current Endpoint Display */}
          <div className="p-3 bg-gray-50 border border-gray-200 rounded">
            <p className="text-xs font-medium text-gray-700 mb-1">Current Endpoint:</p>
            <code className="text-xs text-gray-600 font-mono break-all">
              {currentEndpoint || '(empty)'}
            </code>
          </div>

          {/* Warning if endpoint is empty */}
          {!currentEndpoint && (
            <div className="flex items-start gap-2 p-3 bg-orange-50 border border-orange-200 rounded">
              <AlertCircle className="h-4 w-4 text-orange-600 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-orange-900">
                Endpoint is empty. Please enter an endpoint URL first, or use manual entry.
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleInsert}
            disabled={insertMode === 'manual' && !manualInput.trim()}
          >
            Insert Parameter
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
